import { useCallback, useEffect, useState } from 'react';
import { debounce } from 'lodash';
import { Card } from '../../../../components/card/card';
import { Divider } from '../../../../components/divider/divider';
import { Typography } from '../../../../components/typography/typography';
import { PrettyDescriptor } from '../../../../components/pretty-descriptor/pretty-descriptor';
import { Toggle } from '../../../../components/form/toggle';
import {
  DeliveryConfig,
  DeliveryConfigType,
  ProductDeliveryConfig,
} from '../../../../clients/quipli-service/delivery/delivery';

export const ProductDelivery = ({
  isSaving,
  globalDeliveryConfig,
  productDeliveryConfig,
  setProductDeliveryConfig,
}: {
  isSaving: boolean;
  globalDeliveryConfig: DeliveryConfig;
  productDeliveryConfig: ProductDeliveryConfig;
  setProductDeliveryConfig: (config: ProductDeliveryConfig) => void;
}) => {
  const [enablePickup, setEnablePickup] = useState<boolean>(productDeliveryConfig.allowPickup);
  const [enableShipping, setEnableShipping] = useState<boolean>(productDeliveryConfig.allowShipping);
  const [enableDeliveryFreeText, setEnableDeliveryFreeText] = useState<boolean>(productDeliveryConfig.forceWillCall);
  const [deliveryBaseCost, setDeliveryBaseCost] = useState<number | undefined>(
    productDeliveryConfig?.googleCalculation.baseCost,
  );
  const [deliveryBaseMilesIncluded, setDeliveryBaseMilesIncluded] = useState<number | undefined>(
    productDeliveryConfig?.googleCalculation.includedUnits,
  );
  const [perMileCost, setPerMileCost] = useState<number | undefined>(
    productDeliveryConfig?.googleCalculation.perUnitCost,
  );
  const [flatRateCost, setFlatRateCost] = useState<number>(productDeliveryConfig.flatRate);

  useEffect(() => {
    if (!productDeliveryConfig) return;

    setEnablePickup(productDeliveryConfig?.allowPickup);
    setEnableShipping(productDeliveryConfig?.allowShipping);
    setEnableDeliveryFreeText(productDeliveryConfig?.forceWillCall);
    setDeliveryBaseCost(productDeliveryConfig?.googleCalculation.baseCost);
    setDeliveryBaseMilesIncluded(productDeliveryConfig?.googleCalculation.includedUnits);
    setPerMileCost(productDeliveryConfig?.googleCalculation.perUnitCost);
    setFlatRateCost(productDeliveryConfig.flatRate);
  }, [productDeliveryConfig]);

  useEffect(() => {
    debouncedSetProductDeliveryConfig({
      ...productDeliveryConfig,
      allowPickup: enablePickup,
      allowShipping: enableShipping,
      forceWillCall: enableDeliveryFreeText,
      flatRate: flatRateCost,
      googleCalculation: {
        ...productDeliveryConfig.googleCalculation,
        baseCost: deliveryBaseCost || 0,
        includedUnits: deliveryBaseMilesIncluded || 0,
        perUnitCost: perMileCost || 0,
      },
    });
  }, [
    enablePickup,
    enableShipping,
    enableDeliveryFreeText,
    deliveryBaseCost,
    deliveryBaseMilesIncluded,
    perMileCost,
    flatRateCost,
  ]);

  const debouncedSetProductDeliveryConfig = useCallback(
    debounce((deliveryConfig: ProductDeliveryConfig) => {
      setProductDeliveryConfig(deliveryConfig);
    }, 600),
    [],
  );

  return (
    <Card className="card gap-4 w-auto min-w-[auto] justify-around items-start">
      <div className="part-inventory-title-card">
        <Typography type="text-xl" weight="semi-bold">
          Delivery & Pickup
        </Typography>
        <Divider />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Pickup */}
        <div className="flex flex-col lg:flex-row lg:items-center mt-2">
          <PrettyDescriptor title="Pickup In Store" description={`Enable to make pick up in store available.`} />
        </div>
        <div className="flex flex-col lg:flex-row lg:items-center">
          <Toggle
            color="var(--quipli-yellow-300)"
            data-cy="part-delivery-pickup-toggle"
            checked={productDeliveryConfig.allowPickup}
            onClick={() => setEnablePickup(!enablePickup)}
            disabled={isSaving}
          />
        </div>

        {/* Delivery */}
        <div className="flex flex-col lg:flex-row lg:items-center mt-2">
          <PrettyDescriptor title="Delivery Available" description={`Enable to make delivery available.`} />
        </div>
        <div className="flex flex-col lg:flex-row lg:items-center">
          <Toggle
            color="var(--quipli-yellow-300)"
            data-cy="part-delivery-shipping-toggle"
            checked={productDeliveryConfig.allowShipping}
            onClick={() => setEnableShipping(!enableShipping)}
            disabled={isSaving}
          />
        </div>

        {productDeliveryConfig.allowShipping && (
          <>
            {[DeliveryConfigType.Global, DeliveryConfigType.PerProduct].includes(globalDeliveryConfig.type) &&
              globalDeliveryConfig.allowWillCallAtProductLevel && (
                <>
                  {/* Free Text Delivery Option */}
                  <div className="flex flex-col lg:flex-row lg:items-center mt-2">
                    <PrettyDescriptor
                      title="Free Text Delivery Option"
                      tooltipText={`AKA 'Will Call'. Click to change label.`}
                      tooltipUrl={'labels?keys=L_DELIVERY_WILL_CALL'}
                    />
                  </div>
                  <div className="flex flex-col lg:flex-row lg:items-center">
                    <Toggle
                      color="var(--quipli-yellow-300)"
                      data-cy="part-delivery-free-text-toggle"
                      checked={productDeliveryConfig.forceWillCall}
                      onClick={() => setEnableDeliveryFreeText(!enableDeliveryFreeText)}
                      disabled={isSaving}
                    />
                  </div>
                </>
              )}

            {globalDeliveryConfig.type === DeliveryConfigType.PerProduct &&
              (productDeliveryConfig.useGoogleCalculation ? (
                <>
                  {/* Delivery Base Cost */}
                  <PrettyDescriptor
                    title="Delivery Base Cost"
                    description={`Define the base cost for a one way loaded trip. If this is 0, the global configuration will be used.`}
                  />
                  <div className="mt-4 ml-0 md:ml-4">
                    <input
                      value={deliveryBaseCost}
                      className="conditional-input"
                      type="number"
                      onChange={(e) => setDeliveryBaseCost(Math.abs(parseFloat(e.target.value)))}
                      disabled={isSaving}
                      data-cy="part-delivery-base-cost"
                    />
                  </div>

                  {/* Miles Included In Base Cost */}
                  <PrettyDescriptor
                    title="Miles included in Delivery Base Cost"
                    description={`Define the mileage radius (from your store location) included in the base cost.`}
                  />
                  <div className="mt-4 ml-0 md:ml-4">
                    <input
                      value={deliveryBaseMilesIncluded}
                      className="conditional-input"
                      type="number"
                      onChange={(e) => setDeliveryBaseMilesIncluded(Math.abs(parseFloat(e.target.value)))}
                      disabled={isSaving}
                      data-cy="part-delivery-base-miles-included"
                    />
                  </div>

                  {/* Per Mile Cost */}
                  <PrettyDescriptor
                    title="Per Mile Cost"
                    description={`Define the additional cost per one way loaded mile for deliveries beyond the base cost radius.`}
                  />
                  <div className="mt-4 ml-0 md:ml-4">
                    <input
                      value={perMileCost}
                      className="conditional-input"
                      type="number"
                      onChange={(e) => setPerMileCost(Math.abs(parseFloat(e.target.value)))}
                      disabled={isSaving}
                      data-cy="part-delivery-per-mile-cost"
                    />
                  </div>
                </>
              ) : (
                <>
                  {/* Flat Rate Cost */}
                  <PrettyDescriptor
                    title="Flat Delivery Charge"
                    description={
                      'Define the flat rate for a one way trip (this amount will be doubled for round trip deliveries). If this is 0, the global configuration will be used'
                    }
                  />
                  <div className="mt-4 ml-0 md:ml-4">
                    <input
                      value={flatRateCost}
                      className="conditional-input"
                      type="number"
                      onChange={(e) => setFlatRateCost(Math.abs(parseFloat(e.target.value)))}
                      disabled={isSaving}
                      data-cy="part-flat-rate-cost"
                    />
                  </div>
                </>
              ))}
          </>
        )}
      </div>
    </Card>
  );
};
